import React, { useCallback, useRef, useState, useMemo, useLayoutEffect, useEffect } from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import { AppView } from 'design-system';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import ToolTip from 'design-system/ToolTip';
import StatusBar from 'design-system/StatusBar';
import MoneyInput from 'design-system/MoneyInput';
import AccountCard from 'design-system/AccountCard';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useCashWithdrawableBalance from 'features/pots/hooks/useCashWithdrawableBalance';
import { formatAmount } from '../utils';
import useWithdraw from '../hooks/useWithdraw';
import * as selectors from '../hooks/selectors';
import { getConnectedAccount } from '../actions';
import { MIN_WITHDRAW_AMOUNT } from '../constants';
import { selectAlpacaAccount } from '../reducers/selectors';
import WithdrawConfirmationModal from '../components/WithdrawConfirmationModal';
const strings = {
    pending: 'Pending ',
    navTitle: 'Withdraw',
    button: 'Continue',
    hint: `Available to withdraw `,
    availableToWithdrawTooltipTitle: 'Available to withdraw',
    availableToWithdrawTooltipDesc: 'Money in your account that you can withdraw right now. This may be less than your total balance if you have pending transfers from Pots that haven’t settled yet.',
    unsettledCashTooltipTitle: 'Pending',
    tooltipTextUnsettled: 'Money from transfers from Pots that hasn’t settled yet. You can withdraw it once it settles.',
};
const WithdrawScreen = ({ route, navigation }) => {
    const flow = route.params?.flow;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.navTitle,
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton />,
        });
    });
    const tradingAccount = useAppSelector(selectAlpacaAccount)?.account;
    const cashWithdrawable = useCashWithdrawableBalance();
    const connectedAccount = selectors.useConnectedAccount();
    const cashUnsettled = useAppSelector((store) => store.pots.cashUnsettled);
    useEffect(() => {
        if (!connectedAccount) {
            dispatch(getConnectedAccount());
        }
    }, [connectedAccount]);
    const [disabled, setDisabled] = useState(true);
    const amountRef = useRef(0);
    const withdraw = useWithdraw();
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
        if (value >= MIN_WITHDRAW_AMOUNT && value > 0) {
            setDisabled(false);
        }
        else {
            setDisabled(true);
        }
    }, []);
    const onSubmit = useCallback(() => {
        if (connectedAccount) {
            WithdrawConfirmationModal.show(connectedAccount.ukAccountNumber, connectedAccount.ukSortCode, () => {
                WithdrawConfirmationModal.hide(() => {
                    withdraw(amountRef.current);
                });
            });
        }
    }, [connectedAccount, withdraw]);
    const amountInfo = useMemo(() => {
        let cw;
        let cu;
        if (flow === OnboardingFlow.Pots || flow === OnboardingFlow.Wallet) {
            cw = cashWithdrawable;
            cu = cashUnsettled;
        }
        else {
            cw = tradingAccount?.cashWithdrawable;
            cu = tradingAccount?.cashUnsettled;
        }
        return {
            cw,
            cu,
        };
    }, [cashUnsettled, cashWithdrawable, flow, tradingAccount?.cashUnsettled, tradingAccount?.cashWithdrawable]);
    const handleLearnMore = useCallback(() => {
        Links.openKnownLink('unsettledCashInfo');
    }, []);
    const numPadHeader = useMemo(() => (<View style={styles.numpadHeader}>
        <View style={styles.row}>
          <Text NumbersThin-14 Secondary>
            <Text TextThin-14 Secondary>
              {strings.hint}
            </Text>
            <Text NumbersThin-14 Secondary>
              {formatAmount(amountInfo.cw)}
            </Text>
            <Spacer.W8 />
          </Text>
          <ToolTip title={strings.availableToWithdrawTooltipTitle} subtitle={strings.availableToWithdrawTooltipDesc} colorKey="secondary" small/>
        </View>
        <Spacer h={8}/>
        <View style={styles.row}>
          <Text NumbersThin-14 Secondary>
            <Text TextThin-14 Secondary>
              {strings.pending}
            </Text>
            <Text NumbersThin-14 Secondary>
              {formatAmount(amountInfo.cu)}
            </Text>
            <Spacer.W8 />
          </Text>
          <ToolTip title={strings.unsettledCashTooltipTitle} subtitle={<Text TextThin-14 Secondary centered>
                {strings.tooltipTextUnsettled}{' '}
                <Text Text-14 Primary suppressHighlighting onPress={handleLearnMore}>
                  Learn more
                </Text>
              </Text>} colorKey="secondary" small/>
        </View>
      </View>), [styles.numpadHeader, styles.row, amountInfo.cw, amountInfo.cu, handleLearnMore]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.secondary,
    }), [colors.text.primary, colors.text.secondary]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    return (<AppView avoidHeader>
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        {!!connectedAccount && <AccountCard textOnly investConnectedAccount={connectedAccount}/>}
        <MoneyInput flex colors={moneyInputColors} min={MIN_WITHDRAW_AMOUNT} max={amountInfo.cw?.value} pinPadStyle={styles.pinPad} numPadHeader={numPadHeader} onValueChange={onAmountChange}/>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand disabled={disabled} title={strings.button} onPress={onSubmit}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    withdrawInfoCardContainer: {
        width: '100%',
        marginTop: rem(30),
    },
    pinPad: {
        marginTop: rem(10),
    },
    numpadHeader: {
        flex: 2,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
    },
}));
export default WithdrawScreen;
